(() => {
  const RULES = {
    related: {
      leau: ['theor', 'odosman'],
      theor: ['leau', 'odenui'],
      odosman: ['oratoa', 'daloz'],
      cade: ['daloz', 'leau'],
      odenui: ['odenui_extrait', 'dara'],
      odenui_extrait: ['odenui', 'dara'],
      oratoa: ['odosman', 'dara'],
      dara: ['oratoa', 'odenui', 'daloz'],
      daloz: ['cade', 'odosman', 'dara'],
      tsoly: [],
    },
    silhouette: {
      contemporary: {
        strong: ['leau', 'theor', 'odenui', 'tsoly'],
        secondary: ['odosman', 'daloz'],
      },
      classic: {
        strong: ['odosman', 'cade', 'oratoa'],
        secondary: ['leau', 'odenui'],
      },
      bold: {
        strong: ['odenui_extrait', 'dara', 'daloz', 'tsoly'],
        secondary: ['odenui', 'oratoa'],
      },
    },
    notes: {
      fresh_musky: { strong: ['leau', 'theor'], secondary: ['cade'] },
      powdery_leathery: { strong: ['odosman'], secondary: ['oratoa', 'daloz'] },
      floral_woody: { strong: ['oratoa'], secondary: ['odosman', 'dara'] },
      aromatic_smoky: { strong: ['cade', 'daloz'], secondary: ['oratoa'] },
      amber_gourmand: { strong: ['odenui', 'odenui_extrait'], secondary: ['dara'] },
      oriental_woody: { strong: ['dara', 'daloz'], secondary: ['odenui', 'oratoa'] },
      surprise: { strong: [], secondary: [] },
    },
    intensity: {
      subtle: { strong: ['leau'], secondary: ['theor'] },
      balanced: {
        strong: ['theor', 'odosman', 'cade', 'odenui', 'oratoa', 'dara'],
        secondary: [],
      },
      intense: {
        strong: ['odenui_extrait', 'daloz'],
        secondary: ['odenui', 'dara', 'oratoa'],
      },
    },
  };

  const TIE_BREAK_ORDER = ['q5', 'q3', 'q6', 'q4', 'q2'];

  class FragranceFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.busy = false;
      this.transitionTimer = null;
      this.answers = {};
      this.currentStep = 1;
      this.started = false;
      this.primaryProduct = null;
      this.pendingResults = null;
      this.emailCaptureHandled = false;

      try {
        this.config = JSON.parse(this.querySelector('[data-ff-config]').textContent);
      } catch (error) {
        this.showError(error);
        return;
      }

      this.products = Array.isArray(this.config.products) ? this.config.products : [];
      this.eligibleProducts = this.products.filter((product) => product.eligible === true);
      this.views = new Map(
        [...this.querySelectorAll('[data-ff-view]')].map((view) => [view.dataset.ffView, view])
      );
      this.steps = [...this.querySelectorAll('[data-ff-step]')].sort(
        (a, b) => Number(a.dataset.ffStep) - Number(b.dataset.ffStep)
      );
      this.totalSteps = 6;
      this.sessionKey = `brume-orpin-fragrance-finder:${this.config.sectionId}`;

      this.onClickBound = this.onClick.bind(this);
      this.onSubmitBound = this.onSubmit.bind(this);
      this.onBlockSelectBound = this.onBlockSelect.bind(this);
      this.addEventListener('click', this.onClickBound);
      this.addEventListener('submit', this.onSubmitBound);
      document.addEventListener('shopify:block:select', this.onBlockSelectBound);

      this.buildProductChoices();
      const restored = this.restoreSession();

      if (restored?.completed) {
        this.lastRanking = Array.isArray(restored.scores) ? restored.scores : null;
        const rankedProducts = this.scoreProducts().map((item) => item.product);
        const findProduct = (id) => this.eligibleProducts.find((product) => product.id === id);
        const primary = findProduct(restored.primaryId) || rankedProducts[0];
        const alternativeOne = findProduct(restored.alternativeId)
          || rankedProducts.find((product) => product.id !== primary?.id);
        const storedAlternativeTwo = findProduct(restored.alternativeTwoId);
        const alternativeTwo = storedAlternativeTwo
          && storedAlternativeTwo.id !== primary?.id
          && storedAlternativeTwo.id !== alternativeOne?.id
          ? storedAlternativeTwo
          : rankedProducts.find(
              (product) => product.id !== primary?.id && product.id !== alternativeOne?.id
            );
        if (primary && alternativeOne && alternativeTwo) {
          this.renderResults(primary, alternativeOne, alternativeTwo, false);
          return;
        }
      }

      if (restored?.awaitingEmail) {
        this.started = true;
        this.calculateAndShowResults(false);
        return;
      }

      if (restored?.started) {
        this.started = true;
        this.showView('quiz');
        this.showStep(restored.currentStep || 1, false);
      } else if (this.config.showIntro) {
        this.showView('intro');
      } else {
        this.start(true);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClickBound);
      this.removeEventListener('submit', this.onSubmitBound);
      document.removeEventListener('shopify:block:select', this.onBlockSelectBound);
      window.clearTimeout(this.transitionTimer);
    }

    onClick(event) {
      const restartButton = event.target.closest('[data-ff-restart]');
      if (restartButton && this.contains(restartButton)) {
        this.restart();
        return;
      }

      const startButton = event.target.closest('[data-ff-start]');
      if (startButton && this.contains(startButton)) {
        this.start(true);
        return;
      }

      const backButton = event.target.closest('[data-ff-back]');
      if (backButton && this.contains(backButton)) {
        this.goBack();
        return;
      }

      const emailSkip = event.target.closest('[data-ff-email-skip]');
      if (emailSkip && this.contains(emailSkip)) {
        this.handleEmailSkip();
        return;
      }

      const resultLink = event.target.closest('[data-ff-product-link]');
      if (resultLink && this.contains(resultLink)) {
        const card = resultLink.closest('[data-ff-result-card]');
        const positionMap = { primary: 1, 'secondary-one': 2, 'secondary-two': 3 };
        this.track('finder_product_click', {
          product_id: resultLink.dataset.productId,
          result_position: positionMap[card?.dataset.ffResultCard] || null,
        });
        return;
      }

      const knownButton = event.target.closest('[data-ff-known]');
      if (knownButton && this.contains(knownButton)) {
        this.handleKnownChoice(knownButton.dataset.ffKnown);
        return;
      }

      const knownProduct = event.target.closest('[data-ff-known-product]');
      if (knownProduct && this.contains(knownProduct)) {
        this.answers.knownMode = 'yes';
        this.answers.known = knownProduct.dataset.ffKnownProduct;
        this.setPressed('[data-ff-known-product]', this.answers.known);
        this.trackAnswer('q2', `yes_${this.answers.known}`);
        this.saveSession();
        this.queueAdvance();
        return;
      }

      const colorChoice = event.target.closest('[data-ff-color-product]');
      if (colorChoice && this.contains(colorChoice)) {
        this.handleAnswer('color', colorChoice.dataset.ffColorProduct, 'q4');
        return;
      }

      const answer = event.target.closest('[data-ff-answer]');
      if (answer && this.contains(answer)) {
        const questionMap = { recipient: 'q1', silhouette: 'q3', notes: 'q5', intensity: 'q6' };
        this.handleAnswer(answer.dataset.ffAnswer, answer.dataset.value, questionMap[answer.dataset.ffAnswer]);
      }
    }

    onSubmit(event) {
      const form = event.target.closest('.ff__email-form');
      if (!form || !this.contains(form)) return;
      event.preventDefault();
      this.handleEmailSubmit(form);
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const step = event.target.closest('[data-ff-step]');
      if (!step) return;
      this.started = true;
      this.showView('quiz');
      this.showStep(Number(step.dataset.ffStep), false);
    }

    start(trackEvent = true) {
      if (!this.catalogIsValid()) {
        this.showError(new Error('Not enough eligible products'));
        return;
      }

      this.started = true;
      this.showView('quiz');
      this.showStep(this.currentStep || 1, true);
      this.saveSession();

      if (trackEvent) {
        this.track('finder_start', {
          source: this.config.analyticsSource,
          device: window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop',
        });
      }
    }

    goBack() {
      window.clearTimeout(this.transitionTimer);
      this.busy = false;

      if (this.currentStep <= 1) {
        if (this.config.showIntro) {
          this.showView('intro');
          return;
        }
        return;
      }

      this.showStep(this.currentStep - 1, true);
      this.saveSession();
    }

    restart() {
      const previousPrimary = this.primaryProduct?.id || null;
      window.clearTimeout(this.transitionTimer);
      this.answers = {};
      this.currentStep = 1;
      this.busy = false;
      this.primaryProduct = null;
      this.lastRanking = null;
      this.pendingResults = null;
      this.emailCaptureHandled = false;
      this.clearSession();
      this.resetPressedStates();

      this.track('finder_restart', { previous_primary_product_id: previousPrimary });

      if (this.config.showIntro) {
        this.started = false;
        this.showView('intro');
        this.querySelector('[data-ff-start]')?.focus({ preventScroll: true });
      } else {
        this.start(false);
      }
    }

    handleKnownChoice(value) {
      if (this.busy) return;
      const panel = this.querySelector('[data-ff-known-panel]');
      const yesButton = this.querySelector('[data-ff-known="yes"]');
      const noButton = this.querySelector('[data-ff-known="no"]');

      if (value === 'yes') {
        this.answers.knownMode = 'yes';
        yesButton?.setAttribute('aria-pressed', 'true');
        yesButton?.setAttribute('aria-expanded', 'true');
        noButton?.setAttribute('aria-pressed', 'false');
        if (panel) panel.hidden = false;
        this.saveSession();
        panel?.querySelector('button')?.focus({ preventScroll: true });
        return;
      }

      this.answers.knownMode = 'no';
      this.answers.known = null;
      yesButton?.setAttribute('aria-pressed', 'false');
      yesButton?.setAttribute('aria-expanded', 'false');
      noButton?.setAttribute('aria-pressed', 'true');
      if (panel) panel.hidden = true;
      this.trackAnswer('q2', 'no');
      this.saveSession();
      this.queueAdvance();
    }

    handleAnswer(key, value, questionId) {
      if (this.busy) return;
      this.answers[key] = value;
      this.setPressed(`[data-ff-answer="${key}"]`, value);
      this.trackAnswer(questionId, value);
      this.saveSession();
      this.queueAdvance();
    }

    queueAdvance() {
      if (this.busy) return;
      this.busy = true;
      const delay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 260;
      window.clearTimeout(this.transitionTimer);
      this.transitionTimer = window.setTimeout(() => {
        this.busy = false;
        if (this.currentStep < this.totalSteps) {
          this.showStep(this.currentStep + 1, true);
          this.saveSession();
        } else {
          this.calculateAndShowResults();
        }
      }, delay);
    }

    showStep(stepNumber, focusHeading = true) {
      const target = this.steps.find((step) => Number(step.dataset.ffStep) === stepNumber);
      if (!target) {
        this.showError(new Error(`Missing step ${stepNumber}`));
        return;
      }

      this.currentStep = stepNumber;
      this.steps.forEach((step) => {
        step.hidden = step !== target;
      });

      this.updateKnownQuestion();
      this.restoreStepState();
      this.updateProgress();

      const question = target.querySelector('.ff__question');
      const questionText = question?.textContent?.trim() || '';
      this.announce(`${this.progressText()} — ${questionText}`);

      if (focusHeading && question) {
        question.setAttribute('tabindex', '-1');
        question.focus({ preventScroll: true });
      }
    }

    showView(name) {
      this.views.forEach((view, viewName) => {
        view.hidden = viewName !== name;
      });
    }

    updateProgress() {
      const progress = this.querySelector('.ff__progress');
      progress?.setAttribute('aria-valuenow', String(this.currentStep));
      const label = this.querySelector('[data-ff-progress-label]');
      if (label) label.textContent = this.progressText();
      this.querySelectorAll('[data-ff-progress-segment]').forEach((segment) => {
        const segmentNumber = Number(segment.dataset.ffProgressSegment);
        segment.classList.toggle('is-complete', segmentNumber <= this.currentStep);
      });
    }

    progressText() {
      return (this.config.labels.progress || '[CURRENT] / [TOTAL]')
        .replaceAll('[CURRENT]', String(this.currentStep))
        .replaceAll('[TOTAL]', String(this.totalSteps));
    }

    updateKnownQuestion() {
      const question = this.querySelector('[data-ff-known-question]');
      if (!question) return;
      question.textContent =
        this.answers.recipient === 'gift' ? question.dataset.giftQuestion : question.dataset.selfQuestion;
    }

    restoreStepState() {
      Object.entries(this.answers).forEach(([key, value]) => {
        if (['recipient', 'silhouette', 'notes', 'intensity'].includes(key)) {
          this.setPressed(`[data-ff-answer="${key}"]`, value);
        }
      });

      this.setPressed('[data-ff-color-product]', this.answers.color);
      this.setPressed('[data-ff-known-product]', this.answers.known);

      const knownPanel = this.querySelector('[data-ff-known-panel]');
      const yesButton = this.querySelector('[data-ff-known="yes"]');
      const noButton = this.querySelector('[data-ff-known="no"]');
      const knownYes = this.answers.knownMode === 'yes';
      if (knownPanel) knownPanel.hidden = !knownYes;
      yesButton?.setAttribute('aria-expanded', String(knownYes));
      yesButton?.setAttribute('aria-pressed', String(knownYes));
      noButton?.setAttribute('aria-pressed', String(this.answers.knownMode === 'no'));
    }

    setPressed(selector, selectedValue) {
      this.querySelectorAll(selector).forEach((button) => {
        const buttonValue =
          button.dataset.value || button.dataset.ffColorProduct || button.dataset.ffKnownProduct;
        button.setAttribute('aria-pressed', String(Boolean(selectedValue) && buttonValue === selectedValue));
      });
    }

    resetPressedStates() {
      this.querySelectorAll('[aria-pressed]').forEach((element) => element.setAttribute('aria-pressed', 'false'));
      const knownPanel = this.querySelector('[data-ff-known-panel]');
      if (knownPanel) knownPanel.hidden = true;
      this.querySelector('[data-ff-known="yes"]')?.setAttribute('aria-expanded', 'false');
    }

    buildProductChoices() {
      const knownContainer = this.querySelector('[data-ff-known-products]');
      const colorContainer = this.querySelector('[data-ff-color-choices]');

      this.eligibleProducts.forEach((product) => {
        if (knownContainer) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'ff__product-choice';
          button.dataset.ffKnownProduct = product.id;
          button.setAttribute('aria-pressed', 'false');

          if (product.image) {
            const image = document.createElement('img');
            image.src = product.image;
            image.alt = '';
            image.loading = 'lazy';
            button.append(image);
          }

          const name = document.createElement('span');
          name.textContent = product.name;
          button.append(name);
          knownContainer.append(button);
        }

        if (colorContainer) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'ff__color-choice';
          button.dataset.ffColorProduct = product.id;
          button.setAttribute('aria-pressed', 'false');
          button.setAttribute('aria-label', `${product.colorLabel} — ${product.name}`);

          const swatch = document.createElement('span');
          swatch.className = 'ff__color-swatch';
          swatch.style.setProperty('--swatch', product.colorHex || '#d7d7d7');
          swatch.setAttribute('aria-hidden', 'true');

          const name = document.createElement('span');
          name.className = 'ff__color-name';
          name.textContent = product.colorLabel;

          button.append(swatch, name);
          colorContainer.append(button);
        }
      });
    }

    calculateAndShowResults(trackEmailView = true) {
      try {
        const ranking = this.scoreProducts();
        const [primary, alternativeOne, alternativeTwo] = ranking;
        if (!primary || !alternativeOne || !alternativeTwo) {
          throw new Error('No complete recommendation');
        }

        this.lastRanking = ranking.map((item) => ({
          id: item.product.id,
          total: item.total,
          reasons: item.by,
        }));
        this.pendingResults = [primary.product, alternativeOne.product, alternativeTwo.product];
        this.emailCaptureHandled = false;
        this.showView('email');
        this.saveSession({ awaitingEmail: true, scores: this.lastRanking });

        const emailTitle = this.querySelector('[data-ff-email-title]');
        emailTitle?.setAttribute('tabindex', '-1');
        emailTitle?.focus({ preventScroll: true });
        this.announce(emailTitle?.textContent?.trim() || '');

        if (trackEmailView) this.track('finder_email_view');
      } catch (error) {
        this.showError(error);
      }
    }

    handleEmailSkip() {
      if (this.emailCaptureHandled) return;
      this.track('finder_email_skip');
      this.showPendingResults();
    }

    handleEmailSubmit(form) {
      if (this.emailCaptureHandled) return;
      const input = form.querySelector('input[name="contact[email]"]');
      const email = input?.value?.trim() || '';
      const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

      if (emailIsValid) {
        const formData = new FormData(form);
        this.track('finder_email_submit', { valid: true });
        fetch(form.action, {
          method: 'POST',
          body: formData,
          credentials: 'same-origin',
          headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        })
          .then((response) => {
            this.track('finder_email_saved', { success: response.ok });
          })
          .catch(() => {
            this.track('finder_email_saved', { success: false });
          });
      } else {
        this.track('finder_email_skip', { reason: email ? 'invalid' : 'empty' });
      }

      this.showPendingResults();
    }

    showPendingResults() {
      if (this.emailCaptureHandled) return;
      this.emailCaptureHandled = true;

      if (!Array.isArray(this.pendingResults) || this.pendingResults.length < 3) {
        const ranking = this.scoreProducts();
        this.pendingResults = ranking.slice(0, 3).map((item) => item.product);
      }

      const [primary, alternativeOne, alternativeTwo] = this.pendingResults;
      if (!primary || !alternativeOne || !alternativeTwo) {
        this.showError(new Error('No complete recommendation'));
        return;
      }

      this.pendingResults = null;
      this.renderResults(primary, alternativeOne, alternativeTwo, true);
    }

    scoreProducts() {
      const scored = this.eligibleProducts.map((product, index) => ({
        product,
        index,
        total: 0,
        by: { q2: 0, q3: 0, q4: 0, q5: 0, q6: 0 },
      }));

      const add = (ids, points, signal) => {
        ids.forEach((id) => {
          const item = scored.find((candidate) => candidate.product.id === id);
          if (!item) return;
          item.total += points;
          item.by[signal] += points;
        });
      };

      if (this.answers.knownMode === 'yes' && this.answers.known) {
        add([this.answers.known], -1, 'q2');
        add(RULES.related[this.answers.known] || [], 2, 'q2');
      }

      const silhouette = RULES.silhouette[this.answers.silhouette];
      if (silhouette) {
        add(silhouette.strong, 3, 'q3');
        add(silhouette.secondary, 1, 'q3');
      }

      if (this.answers.color) add([this.answers.color], 2, 'q4');

      const notes = RULES.notes[this.answers.notes];
      if (notes) {
        add(notes.strong, 5, 'q5');
        add(notes.secondary, 2, 'q5');
      }

      const intensity = RULES.intensity[this.answers.intensity];
      if (intensity) {
        add(intensity.strong, 2, 'q6');
        add(intensity.secondary, 1, 'q6');
      }

      return scored.sort((a, b) => {
        if (b.total !== a.total) return b.total - a.total;

        if (this.answers.notes === 'surprise') {
          if (a.product.id === 'odenui' && b.product.id !== 'odenui') return -1;
          if (b.product.id === 'odenui' && a.product.id !== 'odenui') return 1;
        }

        for (const signal of TIE_BREAK_ORDER) {
          if (b.by[signal] !== a.by[signal]) return b.by[signal] - a.by[signal];
        }

        return a.index - b.index;
      });
    }

    renderResults(primary, alternativeOne, alternativeTwo, trackComplete) {
      this.primaryProduct = primary;
      this.renderResultCard('primary', primary);
      this.renderResultCard('secondary-one', alternativeOne);
      this.renderResultCard('secondary-two', alternativeTwo);

      const silhouette = this.answerLabel('silhouette', this.answers.silhouette);
      const notes = this.answerLabel('notes', this.answers.notes);
      const intensity = this.answerLabel('intensity', this.answers.intensity);
      const resultTemplate =
        this.answers.recipient === 'gift' ? this.config.labels.resultGift : this.config.labels.resultSelf;

      const explanation = this.replaceTokens(resultTemplate, {
        SILHOUETTE: this.lowerFirst(silhouette),
        NOTES: this.lowerFirst(notes),
        SILLAGE: this.lowerFirst(intensity),
        PARFUM: primary.name,
        ALTERNATIVE: alternativeOne.name,
        ALTERNATIVE_1: alternativeOne.name,
        ALTERNATIVE_2: alternativeTwo.name,
      });
      const explanationNode = this.querySelector('[data-ff-result-explanation]');
      if (explanationNode) explanationNode.textContent = explanation;

      [
        ['secondary-one', alternativeOne, this.config.labels.alternative],
        ['secondary-two', alternativeTwo, this.config.labels.alternativeTwo],
      ].forEach(([position, product, template]) => {
        const alternativeNode = this.querySelector(
          `[data-ff-result-card="${position}"] [data-ff-alternative-text]`
        );
        if (!alternativeNode) return;
        alternativeNode.textContent = this.replaceTokens(template, {
          ALTERNATIVE: product.name,
          PARFUM: primary.name,
        });
      });

      this.querySelector('[data-ff-match="silhouette"]').textContent = silhouette;
      this.querySelector('[data-ff-match="materials"]').textContent = primary.materials || primary.family;
      this.querySelector('[data-ff-match="intensity"]').textContent = intensity;

      const sampleSlot = this.querySelector('[data-ff-sample-slot]');
      if (sampleSlot) {
        sampleSlot.replaceChildren();
        const template = this.querySelector(`[data-ff-sample-template="${CSS.escape(primary.id)}"]`);
        if (primary.sampleAvailable && template) sampleSlot.append(template.content.cloneNode(true));
      }

      this.showView('results');
      this.announce(
        [primary.name, explanation, alternativeOne.name, alternativeTwo.name]
          .filter(Boolean)
          .join('. ')
      );
      this.saveSession({
        completed: true,
        primaryId: primary.id,
        alternativeId: alternativeOne.id,
        alternativeTwoId: alternativeTwo.id,
        scores: this.lastRanking || null,
      });

      if (trackComplete) {
        this.track('finder_complete', {
          primary_product_id: primary.id,
          alternative_product_id: alternativeOne.id,
          alternative_two_product_id: alternativeTwo.id,
        });
      }

      this.querySelector('.ff__results-title')?.setAttribute('tabindex', '-1');
      this.querySelector('.ff__results-title')?.focus({ preventScroll: true });
    }

    renderResultCard(position, product) {
      const card = this.querySelector(`[data-ff-result-card="${position}"]`);
      if (!card) return;

      const image = card.querySelector('[data-ff-result-image]');
      if (product.image) {
        image.src = product.image;
        image.alt = product.imageAlt || product.name;
      } else {
        image.removeAttribute('src');
        image.alt = '';
      }

      card.querySelector('[data-ff-result-name]').textContent = product.name;
      card.querySelector('[data-ff-result-tagline]').textContent = product.tagline || product.family;

      const link = card.querySelector('[data-ff-product-link]');
      link.href = product.url || '#';
      link.dataset.productId = product.id;
      if (!product.url) link.setAttribute('aria-disabled', 'true');
      else link.removeAttribute('aria-disabled');
      link.querySelector('[data-ff-product-link-label]').textContent = this.replaceTokens(
        this.config.labels.productCta,
        { PARFUM: product.name }
      );
    }

    answerLabel(key, value) {
      const button = this.querySelector(`[data-ff-answer="${key}"][data-value="${CSS.escape(value || '')}"]`);
      return button?.querySelector('.ff__choice-title')?.textContent?.trim() || '';
    }

    replaceTokens(template, values) {
      return Object.entries(values).reduce(
        (text, [token, value]) => text.replaceAll(`[${token}]`, value || ''),
        template || ''
      );
    }

    lowerFirst(text) {
      if (!text) return '';
      return text.charAt(0).toLocaleLowerCase('fr-FR') + text.slice(1);
    }

    catalogIsValid() {
      return this.eligibleProducts.length >= 3 && this.steps.length === this.totalSteps;
    }

    showError(error) {
      console.error('[Fragrance Finder]', error);
      if (this.views) this.showView('error');
      else {
        this.querySelectorAll('[data-ff-view]').forEach((view) => {
          view.hidden = view.dataset.ffView !== 'error';
        });
      }
    }

    trackAnswer(questionId, answerId) {
      this.track('finder_answer', {
        question_id: questionId,
        answer_id: answerId,
        step: this.currentStep,
      });
    }

    track(eventName, parameters = {}) {
      const payload = { event: eventName, ...parameters };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      document.dispatchEvent(new CustomEvent(eventName, { detail: parameters }));
    }

    announce(message) {
      const liveRegion = this.querySelector('[data-ff-live]');
      if (liveRegion) liveRegion.textContent = message;
    }

    saveSession(extra = {}) {
      if (!this.config.persistSession) return;
      const state = {
        started: this.started,
        currentStep: this.currentStep,
        answers: this.answers,
        ...extra,
      };
      try {
        window.sessionStorage.setItem(this.sessionKey, JSON.stringify(state));
      } catch (error) {
        console.warn('[Fragrance Finder] Session could not be saved', error);
      }
    }

    restoreSession() {
      if (!this.config.persistSession) return null;
      try {
        const saved = JSON.parse(window.sessionStorage.getItem(this.sessionKey));
        if (!saved || typeof saved !== 'object') return null;
        this.answers = saved.answers && typeof saved.answers === 'object' ? saved.answers : {};
        this.currentStep = Math.min(this.totalSteps, Math.max(1, Number(saved.currentStep) || 1));
        return saved;
      } catch (error) {
        this.clearSession();
        return null;
      }
    }

    clearSession() {
      try {
        window.sessionStorage.removeItem(this.sessionKey);
      } catch (error) {
        console.warn('[Fragrance Finder] Session could not be cleared', error);
      }
    }
  }

  if (!customElements.get('fragrance-finder')) {
    customElements.define('fragrance-finder', FragranceFinder);
  }
})();
